import type {
    ContentOutput,
    ContentOutputMetadata,
    DefinitionOutput,
    ExecutionUnitOutput,
    ForOutput,
    ForOutputMetadata,
    ForUnitOutput,
    ImportOutputMetadata,
    ImportUnitOutput,
    PluginOutput,
    UseUnitOutput,
} from "@magic-md/core";
import type {
    ContentExecutionEntry,
    ExecutionEntry,
    ForExecutionEntry,
    ImportExecutionEntry,
    UseExecutionEntry,
} from "./execution-state";

interface DefinitionExecutionOptions {
    actionName?: string | null;
    startedAt?: number | null;
    duration?: number | null;
}

interface ForExecutionOptions {
    startedAt?: number | null;
    duration?: number | null;
}

interface ContentExecutionOptions {
    startedAt?: number | null;
    duration?: number | null;
    meta?: ContentExecutionEntry["meta"];
}

interface ImportExecutionOptions {
    startedAt?: number | null;
    duration?: number | null;
    meta?: ImportExecutionEntry["meta"];
    document?: ImportExecutionEntry["document"];
}

interface UseExecutionOptions {
    startedAt?: number | null;
    duration?: number | null;
}

export interface ExecutionMapSet {
    executions: Map<string, ExecutionEntry>;
    forExecutions: Map<string, ForExecutionEntry>;
    contentExecutions: Map<string, ContentExecutionEntry>;
    importExecutions: Map<string, ImportExecutionEntry>;
    useExecutions: Map<string, UseExecutionEntry>;
}

export interface ApplyHydratedUnitOptions {
    mode?: "missing" | "running-or-missing" | "always";
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function getOutputError(
    output: PluginOutput,
): { message?: string; actionName?: string } | null {
    const data = asRecord(output);
    const error = data?.__error;
    return error && typeof error === "object"
        ? (error as { message?: string; actionName?: string })
        : null;
}

function getOutputMetadata(output: PluginOutput): Record<string, unknown> | null {
    const data = asRecord(output);
    const meta = data?.__metadata;
    return meta && typeof meta === "object" ? (meta as Record<string, unknown>) : null;
}

export function buildSyntheticContentExecutionMeta(
    output: ContentOutput,
    options: { startedAt?: string; duration?: number } = {},
): NonNullable<ContentExecutionEntry["meta"]> {
    const total = Object.keys(output).length;
    return {
        total,
        succeeded: total,
        failed: 0,
        duration: options.duration ?? 0,
        startedAt: options.startedAt ?? "",
    };
}

export function buildDefinitionExecutionEntry(
    unitId: string,
    output: PluginOutput,
    options: DefinitionExecutionOptions = {},
): ExecutionEntry {
    const error = getOutputError(output);
    const meta = getOutputMetadata(output);
    return {
        unitId,
        status: error ? "error" : "success",
        actionName: options.actionName ?? (meta?.actionName as string | undefined) ?? error?.actionName ?? null,
        result: error
            ? { success: false, message: error.message ?? "Execution failed" }
            : { success: true, output },
        output,
        startedAt: options.startedAt ?? null,
        duration: options.duration ?? (typeof meta?.duration === "number" ? meta.duration : null),
    };
}

export function buildForExecutionEntry(
    forId: string,
    output: ForOutput | ForUnitOutput,
    options: ForExecutionOptions = {},
): ForExecutionEntry {
    const iterations = "iterations" in output ? output.iterations : [];
    const meta = output.__metadata as ForOutputMetadata | undefined;
    return {
        forId,
        status: (meta?.failed ?? 0) > 0 && (meta?.succeeded ?? 0) === 0 ? "error" : "success",
        totalIterations: meta?.total ?? iterations.length,
        completedIterations: iterations.length,
        iterations: iterations.map((iter, index) => ({
            index: typeof iter.index === "number" ? iter.index : index,
            item: iter.item,
            outputs: (iter.outputs ?? {}) as Record<string, unknown>,
            definitions: iter.definitions,
            contentDefinitions: iter.contentDefinitions,
            success: iter.success ?? true,
            error: iter.error,
        })),
        startedAt: options.startedAt ?? null,
        duration: options.duration ?? meta?.duration ?? null,
        meta: meta
            ? {
                  total: meta.total,
                  succeeded: meta.succeeded,
                  failed: meta.failed,
                  duration: meta.duration,
                  startedAt: meta.startedAt,
              }
            : undefined,
    };
}

export function buildContentExecutionEntry(
    contentId: string,
    output: ContentOutput,
    options: ContentExecutionOptions = {},
): ContentExecutionEntry {
    const error = getOutputError(output as unknown as PluginOutput);
    return {
        contentId,
        status: error ? "error" : "success",
        startedAt: options.startedAt ?? null,
        duration: options.duration ?? options.meta?.duration ?? null,
        output,
        error: error?.message,
        meta: options.meta ?? buildSyntheticContentExecutionMeta(output),
    };
}

export function buildImportExecutionEntry(
    importId: string,
    symbols: ImportExecutionEntry["symbols"],
    options: ImportExecutionOptions = {},
): ImportExecutionEntry {
    return {
        importId,
        status: "success",
        startedAt: options.startedAt ?? null,
        duration: options.duration ?? options.meta?.duration ?? null,
        symbols,
        ...(options.document ? { document: options.document } : {}),
        meta: options.meta,
    };
}

export function buildImportExecutionEntryFromUnitOutput(
    importId: string,
    output: ImportUnitOutput,
    options: ImportExecutionOptions = {},
): ImportExecutionEntry {
    const rawMeta = output.__metadata as ImportOutputMetadata | undefined;
    const derivedMeta = rawMeta
        ? { total: rawMeta.symbolCount, duration: rawMeta.duration, source: rawMeta.source }
        : undefined;
    const document = options.document ?? output.document;
    return buildImportExecutionEntry(importId, output.symbols, {
        ...options,
        meta: options.meta ?? derivedMeta,
        ...(document ? { document } : {}),
    });
}

export function buildUseExecutionEntry(
    useId: string,
    output: Partial<Pick<UseUnitOutput, "materializedId" | "sourceName" | "kind" | "materializedBody">>,
    options: UseExecutionOptions = {},
): UseExecutionEntry {
    return {
        useId,
        status: "success",
        startedAt: options.startedAt ?? null,
        duration: options.duration ?? null,
        materializedId: output.materializedId,
        sourceName: output.sourceName,
        kind: output.kind,
        materializedBody: output.materializedBody,
    };
}

export function hydrateDefinitionExecutionsFromContentOutput(
    executions: Map<string, ExecutionEntry>,
    output: ContentOutput,
    onlyFillMissing = true,
): void {
    for (const [innerDefId, innerOutput] of Object.entries(output)) {
        if (onlyFillMissing && executions.has(innerDefId)) continue;
        const data = asRecord(innerOutput);
        if (!data) continue;
        executions.set(
            innerDefId,
            buildDefinitionExecutionEntry(innerDefId, data as PluginOutput),
        );
    }
}

export function buildDefinitionExecutionEntryFromUnitOutput(
    unitId: string,
    output: DefinitionOutput,
    options: DefinitionExecutionOptions = {},
): ExecutionEntry {
    return buildDefinitionExecutionEntry(unitId, output.data as PluginOutput, options);
}

function hydrateIterationOutputsFromForOutput(
    maps: ExecutionMapSet,
    output: ForOutput | ForUnitOutput,
): void {
    const iterations = "iterations" in output ? output.iterations : [];
    for (const iteration of iterations) {
        for (const [innerId, innerOutput] of Object.entries(iteration.outputs ?? {})) {
            const data = asRecord(innerOutput);
            if (!data) continue;
            const meta = getOutputMetadata(data as PluginOutput);
            const unitType = meta?.unitType;
            const duration = typeof meta?.duration === "number" ? meta.duration : null;
            const startedAt =
                typeof meta?.startedAt === "string" ? meta.startedAt : undefined;

            if (unitType === "content") {
                const contentOutput = data as unknown as ContentOutput;
                maps.contentExecutions.set(
                    innerId,
                    buildContentExecutionEntry(innerId, contentOutput, {
                        duration,
                        meta: {
                            total:
                                typeof meta?.total === "number"
                                    ? meta.total
                                    : Object.keys(contentOutput).filter((key) => !key.startsWith("__")).length,
                            succeeded:
                                typeof meta?.succeeded === "number"
                                    ? meta.succeeded
                                    : Object.keys(contentOutput).filter((key) => !key.startsWith("__")).length,
                            failed: typeof meta?.failed === "number" ? meta.failed : 0,
                            duration: duration ?? 0,
                            startedAt: startedAt ?? "",
                        },
                    }),
                );
                hydrateDefinitionExecutionsFromContentOutput(
                    maps.executions,
                    contentOutput,
                    false,
                );
                continue;
            }

            maps.executions.set(
                innerId,
                buildDefinitionExecutionEntry(innerId, data as PluginOutput, {
                    duration,
                    startedAt: null,
                }),
            );
        }
    }
}

function shouldReplaceDefinitionEntry(
    existing: ExecutionEntry | undefined,
    mode: NonNullable<ApplyHydratedUnitOptions["mode"]>,
): boolean {
    if (mode === "always") return true;
    if (!existing) return true;
    if (mode === "missing") return false;
    return !existing.output;
}

function jsonLikeEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) return true;
    if (typeof left !== typeof right) return false;
    if (left === null || right === null) return left === right;
    if (typeof left !== "object") return false;

    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right)) return false;
        if (left.length !== right.length) return false;
        return left.every((item, index) => jsonLikeEqual(item, right[index]));
    }

    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const isVolatileOutputKey = (key: string) =>
        key === "__metadata" || key === "updatedAt";
    const leftKeys = Object.keys(leftRecord).filter((key) => !isVolatileOutputKey(key));
    const rightKeys = Object.keys(rightRecord).filter((key) => !isVolatileOutputKey(key));
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) =>
        Object.prototype.hasOwnProperty.call(rightRecord, key) &&
        jsonLikeEqual(leftRecord[key], rightRecord[key]),
    );
}

function shouldKeepExistingDefinitionEntryForHydratedOutput(
    existing: ExecutionEntry | undefined,
    output: DefinitionOutput,
    mode: NonNullable<ApplyHydratedUnitOptions["mode"]>,
): boolean {
    return mode === "always" &&
        existing?.status !== "running" &&
        existing?.output !== null &&
        existing?.output !== undefined &&
        jsonLikeEqual(existing.output, output.data);
}

function shouldKeepExistingForEntryForHydratedOutput(
    existing: ForExecutionEntry | undefined,
    output: ForOutput | ForUnitOutput,
    mode: NonNullable<ApplyHydratedUnitOptions["mode"]>,
): boolean {
    return mode === "always" &&
        existing?.status !== "running" &&
        existing !== undefined &&
        jsonLikeEqual(existing.iterations, "iterations" in output ? output.iterations : []);
}

function shouldKeepExistingContentEntryForHydratedOutput(
    existing: ContentExecutionEntry | undefined,
    output: ContentOutput,
    mode: NonNullable<ApplyHydratedUnitOptions["mode"]>,
): boolean {
    return mode === "always" &&
        existing?.status !== "running" &&
        existing?.output !== undefined &&
        jsonLikeEqual(existing.output, output);
}

function shouldKeepExistingImportEntryForHydratedOutput(
    existing: ImportExecutionEntry | undefined,
    output: ImportUnitOutput,
    mode: NonNullable<ApplyHydratedUnitOptions["mode"]>,
): boolean {
    return mode === "always" &&
        existing?.status !== "running" &&
        existing !== undefined &&
        jsonLikeEqual(existing.symbols, output.symbols) &&
        jsonLikeEqual(existing.document, output.document);
}

function shouldKeepExistingUseEntryForHydratedOutput(
    existing: UseExecutionEntry | undefined,
    output: Partial<Pick<UseUnitOutput, "materializedId" | "sourceName" | "kind" | "materializedBody">>,
    mode: NonNullable<ApplyHydratedUnitOptions["mode"]>,
): boolean {
    return mode === "always" &&
        existing?.status !== "running" &&
        existing !== undefined &&
        existing.materializedId === output.materializedId &&
        existing.sourceName === output.sourceName &&
        existing.kind === output.kind &&
        existing.materializedBody === output.materializedBody;
}

function shouldReplaceStatusEntry(
    existing:
        | ForExecutionEntry
        | ContentExecutionEntry
        | ImportExecutionEntry
        | UseExecutionEntry
        | undefined,
    mode: NonNullable<ApplyHydratedUnitOptions["mode"]>,
): boolean {
    if (mode === "always") return true;
    if (!existing) return true;
    if (mode === "missing") return false;
    return existing.status === "running";
}

export function applyHydratedUnitOutputToExecutionMaps(
    maps: ExecutionMapSet,
    unitId: string,
    rawOutput: ExecutionUnitOutput,
    options: ApplyHydratedUnitOptions = {},
): void {
    const mode = options.mode ?? "missing";

    if (rawOutput.unitType === "definition") {
        const prev = maps.executions.get(unitId);
        if (!shouldReplaceDefinitionEntry(prev, mode)) return;
        if (shouldKeepExistingDefinitionEntryForHydratedOutput(prev, rawOutput, mode)) return;
        maps.executions.set(
            unitId,
            buildDefinitionExecutionEntryFromUnitOutput(unitId, rawOutput, {
                actionName: prev?.actionName ?? null,
                startedAt: prev?.startedAt ?? null,
                duration: prev?.duration ?? null,
            }),
        );
        return;
    }

    if (rawOutput.unitType === "for") {
        const prev = maps.forExecutions.get(unitId);
        if (!shouldReplaceStatusEntry(prev, mode)) return;
        if (shouldKeepExistingForEntryForHydratedOutput(prev, rawOutput, mode)) {
            hydrateIterationOutputsFromForOutput(maps, rawOutput);
            return;
        }
        maps.forExecutions.set(
            unitId,
            buildForExecutionEntry(unitId, rawOutput, {
                startedAt: prev?.startedAt ?? null,
                duration: prev?.duration ?? null,
            }),
        );
        hydrateIterationOutputsFromForOutput(maps, rawOutput);
        return;
    }

    if (rawOutput.unitType === "content") {
        const prev = maps.contentExecutions.get(unitId);
        if (!shouldReplaceStatusEntry(prev, mode)) return;
        if (shouldKeepExistingContentEntryForHydratedOutput(prev, rawOutput.data, mode)) return;
        const contentMeta = rawOutput.__metadata as ContentOutputMetadata | undefined;
        maps.contentExecutions.set(
            unitId,
            buildContentExecutionEntry(unitId, rawOutput.data, {
                startedAt: prev?.startedAt ?? null,
                duration: prev?.duration ?? null,
                meta: contentMeta
                    ? {
                          total: contentMeta.total,
                          succeeded: contentMeta.succeeded,
                          failed: contentMeta.failed,
                          duration: contentMeta.duration,
                          startedAt: contentMeta.startedAt,
                      }
                    : undefined,
            }),
        );
        return;
    }

    if (rawOutput.unitType === "import") {
        const prev = maps.importExecutions.get(unitId);
        if (!shouldReplaceStatusEntry(prev, mode)) return;
        if (shouldKeepExistingImportEntryForHydratedOutput(prev, rawOutput, mode)) return;
        maps.importExecutions.set(
            unitId,
            buildImportExecutionEntryFromUnitOutput(unitId, rawOutput, {
                startedAt: prev?.startedAt ?? null,
                duration: prev?.duration ?? null,
            }),
        );
        return;
    }

    const prevUse = maps.useExecutions.get(unitId);
    if (shouldReplaceStatusEntry(prevUse, mode)) {
        if (!shouldKeepExistingUseEntryForHydratedOutput(prevUse, rawOutput, mode)) {
            maps.useExecutions.set(
                unitId,
                buildUseExecutionEntry(unitId, rawOutput, {
                    startedAt: prevUse?.startedAt ?? null,
                    duration: prevUse?.duration ?? null,
                }),
            );
        }
    }

    if (rawOutput.kind === "node" && rawOutput.nodeOutput) {
        const prevDefinition = maps.executions.get(unitId);
        if (shouldReplaceDefinitionEntry(prevDefinition, mode)) {
            maps.executions.set(
                unitId,
                buildDefinitionExecutionEntry(unitId, rawOutput.nodeOutput, {
                    actionName: prevDefinition?.actionName ?? null,
                    startedAt: prevDefinition?.startedAt ?? null,
                    duration: prevDefinition?.duration ?? null,
                }),
            );
        }
        return;
    }

    if (rawOutput.kind === "content" && rawOutput.contentOutput) {
        const prevContent = maps.contentExecutions.get(unitId);
        // Use only prevContent (not prevUse) to guard this set — a prevUse entry from
        // restoreFromParsedOutput may have kind=undefined (created from a stub output before
        // the engine response arrived) and must not block populating contentExecutions.
        if (shouldReplaceStatusEntry(prevContent, mode)) {
            if (!shouldKeepExistingContentEntryForHydratedOutput(prevContent, rawOutput.contentOutput, mode)) {
                maps.contentExecutions.set(
                    unitId,
                    buildContentExecutionEntry(unitId, rawOutput.contentOutput, {
                        startedAt: prevContent?.startedAt ?? prevUse?.startedAt ?? null,
                        duration: prevContent?.duration ?? prevUse?.duration ?? null,
                        meta: buildSyntheticContentExecutionMeta(rawOutput.contentOutput, {
                            startedAt: new Date().toISOString(),
                        }),
                    }),
                );
            }
        }
        hydrateDefinitionExecutionsFromContentOutput(
            maps.executions,
            rawOutput.contentOutput,
        );
        return;
    }

    if (rawOutput.kind === "for" && rawOutput.forOutput) {
        const prevFor = maps.forExecutions.get(unitId);
        if (shouldReplaceStatusEntry(prevFor, mode)) {
            if (!shouldKeepExistingForEntryForHydratedOutput(prevFor, rawOutput.forOutput, mode)) {
                maps.forExecutions.set(
                    unitId,
                    buildForExecutionEntry(unitId, rawOutput.forOutput, {
                        startedAt: prevFor?.startedAt ?? prevUse?.startedAt ?? null,
                        duration: prevFor?.duration ?? prevUse?.duration ?? null,
                    }),
                );
            }
        }
        hydrateIterationOutputsFromForOutput(maps, rawOutput.forOutput);
    }
}
